import { useState, useEffect } from "react";
import { useThemeContext } from "../theme/ThemeContext";
import { useLockBodyScroll } from "../hooks/useLockBodyScroll";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { AirlineLogo, Button } from "./shared";
import { overlayBackdrop } from "../theme/styles";
import { useSwipeToClose } from "../hooks/useSwipeToClose";
import { dbLoadContacts, dbUpsertContactPhone } from "../db";
import { TOAST_DURATION_MS } from "../utils/constants";

const DIR_AIRLINES = [
  { code: "UA",  name: "United Airlines" },
  { code: "AC",  name: "Air Canada" },
  { code: "AV",  name: "Avianca" },
  { code: "AZ",  name: "ITA Airways" },
  { code: "BR",  name: "EVA Air" },
  { code: "CA",  name: "Air China" },
  { code: "CM",  name: "Copa Airlines" },
  { code: "LHG", name: "Lufthansa Group" },
  { code: "LO",  name: "LOT Polish Airlines" },
  { code: "NH",  name: "All Nippon Airways (ANA)" },
  { code: "NZ",  name: "Air New Zealand" },
  { code: "OZ",  name: "Asiana Airlines" },
  { code: "SQ",  name: "Singapore Airlines" },
  { code: "TK",  name: "Turkish Airlines" },
  { code: "TP",  name: "TAP Air Portugal" },
];

const UA_DEPT_ROLES      = ["Star Connect Coordinator", "Operations", "Ticketing", "Baggage Service", "Duty Manager"];
const PARTNER_DEPT_ROLES = ["Operations", "Passenger Transfer", "Ticketing", "Baggage Service", "Duty Manager"];
const getDeptRoles = (code) => code === "UA" ? UA_DEPT_ROLES : PARTNER_DEPT_ROLES;

const isEmail = (val) => typeof val === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

const parsePhoneExt = (val) => {
  if (!val) return { base: "", ext: null };
  const m = val.trim().match(/^(.*?)\s*(?:x|ext\.?|extension)\s*(\d+)\s*$/i);
  if (m) return { base: m[1].trim(), ext: m[2] };
  return { base: val.trim(), ext: null };
};

const buildTelHref = (val) => {
  const { base, ext } = parsePhoneExt(val);
  const digits = base.replace(/[^\d+]/g, "");
  return ext ? `tel:${digits},${ext}` : `tel:${digits}`;
};

const getFieldHint = (airline, dept) => {
  if (airline === "NZ" && dept === "Baggage Service")
    return { placeholder: "e.g. baggage@airnz.co.nz", hint: "Email address — will open mail app" };
  if (airline === "AV" && (dept === "Passenger Transfer" || dept === "Ticketing" || dept === "Baggage Service"))
    return { placeholder: "e.g. +1 310 555 0100 x4567", hint: "Include extension: …number x1234" };
  return { placeholder: "e.g. +1 310 555 0100", hint: "Include country code for direct dial" };
};

export function PhoneDirectoryModal({ isUA, myAirline, isAdmin, onClose }) {
  const { SA, GLASS, tokens, TYPOGRAPHY, RADIUS } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const { handleProps, dragStyle } = useSwipeToClose(onClose);

  const [contacts,   setContacts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null);
  const [editMode,   setEditMode]   = useState(false);
  const [phoneEdits, setPhoneEdits] = useState({});
  const [saving,     setSaving]     = useState({});
  const [saved,      setSaved]      = useState({});

  useEffect(() => {
    dbLoadContacts().then(rows => {
      setContacts(rows);
      setLoading(false);
    });
  }, []);

  const contactMap = {};
  contacts.forEach(c => {
    if (!contactMap[c.airline]) contactMap[c.airline] = {};
    contactMap[c.airline][c.name] = c.phone || "";
  });

  const getPhoneLines = (airline, dept) => {
    const raw = contactMap[airline]?.[dept] || "";
    return raw.split("\n").map(s => s.trim()).filter(Boolean);
  };

  const getEditLines = (airline, dept) => {
    if (phoneEdits[airline] && phoneEdits[airline][dept] !== undefined)
      return phoneEdits[airline][dept];
    const savedLines = getPhoneLines(airline, dept);
    return savedLines.length > 0 ? savedLines : [""];
  };

  const setEditLines = (airline, dept, lines) => {
    setPhoneEdits(prev => ({ ...prev, [airline]: { ...(prev[airline] || {}), [dept]: lines } }));
  };

  const handleToggle      = (code) => setExpanded(prev => prev === code ? null : code);
  const handleLineChange  = (airline, dept, idx, val) => { const lines = [...getEditLines(airline, dept)]; lines[idx] = val; setEditLines(airline, dept, lines); };
  const handleAddLine     = (airline, dept) => setEditLines(airline, dept, [...getEditLines(airline, dept), ""]);
  const handleRemoveLine  = (airline, dept, idx) => { const lines = getEditLines(airline, dept).filter((_, i) => i !== idx); setEditLines(airline, dept, lines.length > 0 ? lines : [""]); };

  const handleSave = async (airline, dept) => {
    const key = airline + "|" + dept;
    const lines = getEditLines(airline, dept).map(s => s.trim()).filter(Boolean);
    const combined = lines.join("\n");
    setSaving(p => ({ ...p, [key]: true }));
    const ok = await dbUpsertContactPhone(airline, dept, combined);
    setSaving(p => ({ ...p, [key]: false }));
    if (!ok) {
      setSaved(p => ({ ...p, [key]: "error" }));
      setTimeout(() => setSaved(p => ({ ...p, [key]: false })), TOAST_DURATION_MS * 1.5);
      return;
    }
    setContacts(prev => {
      const exists = prev.find(c => c.airline === airline && c.name === dept);
      if (exists) return prev.map(c => c.airline === airline && c.name === dept ? { ...c, phone: combined } : c);
      return [...prev, { id: Date.now(), airline, name: dept, phone: combined }];
    });
    setPhoneEdits(prev => {
      const updated = { ...prev };
      if (updated[airline]) { updated[airline] = { ...updated[airline] }; delete updated[airline][dept]; }
      return updated;
    });
    setSaved(p => ({ ...p, [key]: true }));
    setTimeout(() => setSaved(p => ({ ...p, [key]: false })), TOAST_DURATION_MS);
  };

  const phoneIcon = (color = SA.accent) => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 3.5C2.5 2.948 2.948 2.5 3.5 2.5H5.5C5.776 2.5 6 2.724 6 3L6.75 5.75C6.75 6.026 6.526 6.25 6.25 6.25L4.75 6.75C5.493 8.561 6.939 9.843 8.75 10.75L9.25 9.25C9.474 8.974 9.698 8.75 9.974 8.75L12.75 9.5C13.026 9.5 13.25 9.724 13.25 10V12C13.25 12.552 12.802 13 12.25 13C6.865 13 2.5 8.635 2.5 3.5Z" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const mailIcon = (color = SA.accent) => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke={color} strokeWidth="1.3"/>
      <path d="M1.5 5.5L8 9.5L14.5 5.5" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );

  const inputSt = {
    background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
    borderRadius: RADIUS.sm, color: SA.white, padding: "6px 10px", fontSize: TYPOGRAPHY.body,
    fontFamily: "'Barlow',sans-serif", outline: "none", width: "100%",
  };

  return (
    <div style={{ ...overlayBackdrop, zIndex: 400,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}
      onClick={onClose}>
      <div style={{ ...GLASS.modal,
        width: "100%", maxWidth: 520, maxHeight: "78vh",
        borderRadius: "16px 16px 0 0", display: "flex", flexDirection: "column",
        animation: "filterSheetUp 0.24s cubic-bezier(0.16,1,0.3,1)", ...dragStyle }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                      flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
        </div>
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: SA.white }}>Phone Directory</div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2 }}>Tap an airline to expand · Tap a number to call</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {isAdmin && (
              <Button variant="pill" active={editMode} onClick={() => setEditMode(e => !e)}
                style={{ padding: "6px 12px", fontSize: TYPOGRAPHY.caption, fontWeight: 600 }}>
                {editMode ? "Done" : "Edit"}
              </Button>
            )}
            <Button variant="icon" onClick={onClose}
              style={{ background: "rgba(255,255,255,0.07)", color: SA.silver, width: 30, height: 30, fontSize: 16 }}>✕</Button>
          </div>
        </div>

        {/* Scrollable list */}
        <div style={{ overflowY: "auto", flex: 1, paddingBottom: 20 }}>
          {loading ? (
            <div style={{ padding: "32px 20px", textAlign: "center", color: SA.borderGray, fontSize: TYPOGRAPHY.note }}>
              Loading…
            </div>
          ) : (
            DIR_AIRLINES.map(({ code, name }) => {
              const isOpen    = expanded === code;
              const deptCount = getDeptRoles(code).filter(d => getPhoneLines(code, d).length > 0).length;

              return (
                <div key={code} style={{ borderBottom: `1px solid ${SA.borderGray}` }}>
                  {/* Airline row */}
                  <div onClick={() => handleToggle(code)}
                    style={{ display: "flex", alignItems: "center", padding: "13px 20px",
                      cursor: "pointer", background: isOpen ? "rgba(74,124,247,0.06)" : "transparent",
                      transition: "background 0.15s" }}>
                    <AirlineLogo iata={code} size={28} />
                    <div style={{ marginLeft: 12, flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: TYPOGRAPHY.note, fontWeight: 600, color: SA.white }}>{code}</div>
                      <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 1,
                        display: "flex", alignItems: "center", gap: 5 }}>
                        {name}
                        {code === "UA" && <span style={{ fontSize: 7, letterSpacing: "0.10em",
                          textTransform: "uppercase", color: SA.accent,
                          background: "rgba(91,142,255,0.12)", border: "1px solid rgba(91,142,255,0.30)",
                          borderRadius: RADIUS.sharp, padding: "2px 5px",
                          fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 700 }}>SCCO</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {deptCount > 0 && !isOpen && (
                        <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, background: SA.darkGray,
                          border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.md, padding: "2px 8px" }}>
                          {deptCount} number{deptCount !== 1 ? "s" : ""}
                        </div>
                      )}
                      <div style={{ color: SA.silver, fontSize: TYPOGRAPHY.body, transition: "transform 0.2s",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</div>
                    </div>
                  </div>

                  {/* Expanded dept rows */}
                  {isOpen && (
                    <div style={{ background: SA.darkGray, borderTop: `1px solid rgba(46,51,64,0.7)` }}>
                      {getDeptRoles(code).map((dept, deptIdx) => {
                        const phoneLines = getPhoneLines(code, dept);
                        const editLines  = getEditLines(code, dept);
                        const key        = code + "|" + dept;
                        const isSaving   = saving[key];
                        const isSaved    = saved[key];
                        const isPinned   = code === "UA" && deptIdx === 0;

                        return (
                          <div key={dept} style={{
                            padding: "12px 20px 12px 28px",
                            borderBottom: `1px solid rgba(46,51,64,0.5)`,
                            background: isPinned ? "rgba(255,215,0,0.04)" : "transparent",
                            borderLeft: isPinned ? "3px solid rgba(255,215,0,0.55)" : "3px solid transparent",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", marginBottom: editMode ? 8 : phoneLines.length > 0 ? 8 : 0 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: TYPOGRAPHY.note, color: isPinned ? "#FFD700" : SA.silver, fontWeight: isPinned ? 700 : 500 }}>{dept}</div>
                              </div>
                              {editMode && isAdmin && (
                                <Button onClick={() => handleSave(code, dept)} disabled={isSaving}
                                  style={{ padding: "5px 12px", borderRadius: RADIUS.sm, fontSize: TYPOGRAPHY.caption,
                                    fontWeight: 600, whiteSpace: "nowrap", border: "none",
                                    background: isSaved === "error" ? tokens.dangerBg : isSaved ? "rgba(110,196,114,0.2)" : SA.medGray,
                                    color: isSaved === "error" ? tokens.danger : isSaved ? tokens.success : SA.lightSilver }}>
                                  {isSaving ? "…" : isSaved === "error" ? "✗ Error" : isSaved ? "✓ Saved" : "Save"}
                                </Button>
                              )}
                            </div>

                            {editMode && isAdmin ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {editLines.map((line, idx) => {
                                  const { placeholder, hint } = getFieldHint(code, dept);
                                  return (
                                    <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                        <input
                                          value={line}
                                          onChange={e => handleLineChange(code, dept, idx, e.target.value)}
                                          placeholder={idx === 0 ? placeholder : "Additional number…"}
                                          style={{ ...inputSt, flex: 1 }}
                                        />
                                        {editLines.length > 1 && (
                                          <Button variant="ghost" onClick={() => handleRemoveLine(code, dept, idx)}
                                            style={{ color: SA.borderGray, fontSize: 16, padding: "0 4px", lineHeight: 1, flexShrink: 0 }}>×</Button>
                                        )}
                                      </div>
                                      {idx === 0 && (
                                        <div style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, letterSpacing: "0.03em" }}>{hint}</div>
                                      )}
                                    </div>
                                  );
                                })}
                                <Button variant="ghost" onClick={() => handleAddLine(code, dept)}
                                  style={{ alignSelf: "flex-start",
                                    border: `1px dashed ${SA.borderGray}`, borderRadius: RADIUS.sm,
                                    color: SA.silver, fontSize: TYPOGRAPHY.caption, padding: "4px 10px",
                                    marginTop: 2 }}>
                                  + Add number
                                </Button>
                              </div>
                            ) : phoneLines.length > 0 ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {phoneLines.map((ph, idx) => (
                                  isEmail(ph) ? (
                                    <a key={idx} href={"mailto:" + ph}
                                      style={{ display: "inline-flex", alignItems: "center", gap: 7,
                                        textDecoration: "none", alignSelf: "flex-start",
                                        background: "rgba(74,124,247,0.1)",
                                        border: `1px solid rgba(74,124,247,0.3)`,
                                        borderRadius: RADIUS.pill, padding: "6px 12px", maxWidth: "100%" }}>
                                      {mailIcon()}
                                      <span style={{ fontSize: TYPOGRAPHY.note, color: SA.accent,
                                        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 600,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ph}</span>
                                    </a>
                                  ) : (() => {
                                    const { base, ext } = parsePhoneExt(ph);
                                    return (
                                      <a key={idx} href={buildTelHref(ph)}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 7,
                                          textDecoration: "none", alignSelf: "flex-start",
                                          background: "rgba(74,124,247,0.1)",
                                          border: `1px solid rgba(74,124,247,0.3)`,
                                          borderRadius: RADIUS.pill, padding: "6px 12px" }}>
                                        {phoneIcon()}
                                        <span style={{ fontSize: 16, color: SA.accent,
                                          fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>{base}</span>
                                        {ext && (
                                          <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver,
                                            fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 500 }}>x{ext}</span>
                                        )}
                                      </a>
                                    );
                                  })()
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.borderGray, fontStyle: "italic" }}>
                                No number
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
